import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { 
  LayoutDashboard, Users, FileText, Settings, LogOut, 
  BookOpen, BarChart3, UserCheck, Layers, ChevronLeft, ChevronRight, Menu, Clock
} from "lucide-react";
import wwgsLogo from "../assets/wwags-logo.jpg";

const menuItems = [
  { path: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/candidates", icon: UserCheck, label: "Candidates" },
  { path: "/admin/questions", icon: BookOpen, label: "Questions" },
  { path: "/admin/question-sets", icon: Layers, label: "Question Sets" },
  { path: "/admin/reports", icon: BarChart3, label: "Reports" },
  { path: "/admin/exam-settings", icon: Clock, label: "Exam Settings" },
  { path: "/admin/users", icon: Users, label: "Manage Users" },
];

export default function AdminSidebar({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const adminName = localStorage.getItem("adminName") || "Admin";

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminName");
    navigate("/admin");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile Menu Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-[60] p-2 bg-white rounded-lg shadow-md border"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5 text-gray-600" />
      </button>

      {/* Sidebar Overlay for Mobile */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - Fixed position */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-50
          ${collapsed ? "w-20" : "w-64"} 
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          bg-gradient-to-b from-emerald-900 to-emerald-800 
          text-white transition-all duration-300 flex flex-col
        `}
      >
        {/* Header */}
        <div className={`p-4 border-b border-emerald-700 ${collapsed ? "text-center" : ""}`}>
          {!collapsed && (
            <div className="flex items-center gap-3">
              <img 
                src={wwgsLogo} 
                alt="WWAGS Logo" 
                className="w-10 h-10 object-contain rounded-full border border-emerald-500"
                data-testid="sidebar-logo"
              />
              <div>
                <h1 className="text-base font-bold">WWAGS Admin</h1>
                <p className="text-[10px] text-emerald-300">CRP-EP Exam Portal</p>
              </div>
            </div>
          )}
          {collapsed && (
            <img 
              src={wwgsLogo} 
              alt="WWAGS" 
              className="w-10 h-10 object-contain rounded-full border border-emerald-500 mx-auto"
              data-testid="sidebar-logo-collapsed"
            />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all
                ${isActive(item.path) 
                  ? "bg-white/20 text-white" 
                  : "text-emerald-200 hover:bg-white/10 hover:text-white"
                }
                ${collapsed ? "justify-center" : ""}
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              title={collapsed ? item.label : ""}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-emerald-700 space-y-2">
          {!collapsed && (
            <div className="px-3 py-2 text-xs text-emerald-300">
              <p>Logged in as:</p>
              <p className="font-medium text-white truncate">{adminName}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
              text-red-300 hover:bg-red-500/20 hover:text-red-200 transition-all
              ${collapsed ? "justify-center" : ""}
            `}
            data-testid="logout-btn"
            title={collapsed ? "Logout" : ""}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>

        {/* Developer Credit Footer */}
        {!collapsed && (
          <div className="p-3 border-t border-emerald-700 bg-emerald-950/50">
            <p className="text-[10px] text-emerald-400 leading-tight text-center">
              Portal Developed by<br />
              <span className="text-emerald-300 font-medium">
                Institute of Skill Development and Entrepreneurship (ISDE), Manipur
              </span>
              <br />
              <span className="text-emerald-500 text-[9px]">
                (An Institute run under Shri Suvakumar Singh Memorial Trust)
              </span>
            </p>
          </div>
        )}

        {/* Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-emerald-700 rounded-full items-center justify-center text-white hover:bg-emerald-600 shadow-lg"
          data-testid="collapse-sidebar-btn"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Main Content - with left margin to account for fixed sidebar, top padding on mobile for menu button */}
      <main className={`flex-1 min-h-screen overflow-x-hidden transition-all duration-300 pt-14 lg:pt-0 ${collapsed ? "lg:ml-20" : "lg:ml-64"}`}>
        {children}
      </main>
    </div>
  );
}
